import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getConfigurationBySenior, updateBatteryAlertFlags, Configuration } from '../../lib/supabase';
import { defaultBatteryAlertFlags, isMemoryProfile, formatBatteryAlertState } from '../../constants/batteryAlerts';

interface Props {
  isOpen: boolean;
  seniorId: string;
  seniorName: string;
  profileType?: string | null;
  caregiverPhone?: string | null;
  caregiverSmsOptedOut?: boolean;
  onClose: () => void;
  /** Fired after a toggle is persisted, so the dashboard tile can re-read state. */
  onFlagsUpdated?: () => void;
}

/**
 * F05 brief — per-senior battery alert toggles.
 *
 *   1. Voice Assistant reminds [Senior] to charge (40% layer)
 *      Defaults ON, except memory/cognitive profiles (the senior may not
 *      respond reliably to a voice nag, so the caregiver enables it
 *      deliberately).
 *
 *   2. Text me when battery hits 25% (caregiver SMS layer)
 *      Defaults ON for every profile. Requires caregivers.phone present and
 *      caregivers.sms_opted_out=false (the CASL opt-in). Both gates surfaced
 *      to the caregiver inline so they understand why an enabled toggle may
 *      still not fire.
 *
 * The defaults live in constants/batteryAlerts.ts and are shared with new-senior
 * creation, so what the wizard writes and what this drawer would fall back to
 * can't drift apart. A senior whose row already stores `false` keeps `false` —
 * `??` only fills in a missing value.
 */
export const BatteryAlertsDrawer: FC<Props> = ({
  isOpen, seniorId, seniorName, profileType, caregiverPhone, caregiverSmsOptedOut, onClose,
  onFlagsUpdated,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<'voice' | 'sms' | null>(null);
  const [voiceNagEnabled, setVoiceNagEnabled] = useState<boolean>(true);
  const [lowbatSmsEnabled, setLowbatSmsEnabled] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const isCognitive = isMemoryProfile(profileType);
  const smsGateBlocked = !caregiverPhone || caregiverSmsOptedOut === true;

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getConfigurationBySenior(seniorId).then((cfg) => {
      if (cancelled) return;
      const defaults = defaultBatteryAlertFlags(profileType);
      setVoiceNagEnabled(cfg?.voice_nag_enabled ?? defaults.voice_nag_enabled);
      setLowbatSmsEnabled(cfg?.lowbat_sms_enabled ?? defaults.lowbat_sms_enabled);
      setLoading(false);
    }).catch((e) => {
      if (cancelled) return;
      console.error('[BatteryAlertsDrawer] load failed', e);
      setError('Could not load battery alert settings.');
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [isOpen, seniorId, profileType]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const toggle = async (which: 'voice' | 'sms', next: boolean) => {
    setSaving(which);
    setError(null);
    try {
      if (which === 'voice') {
        await updateBatteryAlertFlags(seniorId, { voice_nag_enabled: next });
        setVoiceNagEnabled(next);
      } else {
        await updateBatteryAlertFlags(seniorId, { lowbat_sms_enabled: next });
        setLowbatSmsEnabled(next);
      }
      // Only after the write succeeded — the dashboard tile reads the persisted
      // row, so telling it to refetch on a failed save would show stale state.
      onFlagsUpdated?.();
    } catch (e) {
      console.error('[BatteryAlertsDrawer] update failed', e);
      setError('Could not save change. Please try again.');
    } finally {
      setSaving(null);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop — z above Leaflet's internal 1000 controls so the drawer
          isn't undercut by zoom buttons / attribution links bleeding through. */}
      <div
        className="fixed inset-0 z-[1090] bg-brand-text/40 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
      />
      {/* Drawer panel */}
      <div
        className="fixed top-0 right-0 bottom-0 z-[1100] w-full sm:w-[420px] bg-white shadow-2xl
                   flex flex-col animate-slideInRight border-l border-brand-border"
        role="dialog"
        aria-labelledby="battery-alerts-drawer-title"
      >
        <div className="flex items-center justify-between p-5 border-b border-brand-border shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <span className="material-symbols-outlined text-amber-600" style={{ fontSize: '22px' }}>
                battery_alert
              </span>
            </div>
            <div>
              <h2 id="battery-alerts-drawer-title" className="font-display text-lg font-bold text-brand-text leading-tight">
                Battery alerts
              </h2>
              <p className="text-xs text-brand-text-muted mt-0.5">For {seniorName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-brand-bg-warm transition-colors"
            aria-label="Close"
          >
            <span className="material-symbols-outlined text-brand-text-muted">close</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading && (
            <div className="text-center text-sm text-brand-text-muted py-8">Loading…</div>
          )}

          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}

          {!loading && (
            <>
              {/* D13 — the composed state moved here from the dashboard tile.
                  On a tile, "SMS 25%" reads like a battery level; in the panel,
                  next to the two toggles it describes, it reads as the alert
                  thresholds it actually is. This also keeps the memory-profile
                  mixed state ("On — SMS 25%") visible somewhere, which a bare
                  ON/OFF tile cannot express. */}
              <p className="text-sm font-semibold text-brand-text -mt-1">
                {formatBatteryAlertState(voiceNagEnabled, lowbatSmsEnabled, {
                  on: t('dashboard.actions.battery.state.on'),
                  off: t('dashboard.actions.battery.state.off'),
                  voice: t('dashboard.actions.battery.state.voice'),
                  sms: t('dashboard.actions.battery.state.sms'),
                })}
              </p>

              {/* Voice nag toggle */}
              <section className="rounded-2xl border border-brand-border p-4 bg-white">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-brand-text leading-tight">
                      Voice Assistant reminds {seniorName} to charge
                    </h3>
                    <p className="text-xs text-brand-text-muted mt-1">
                      Spoken nudge starting at 40% battery, repeated every 30 minutes for up to 3 reminders.
                      Never plays while charging.
                    </p>
                    {isCognitive && (
                      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg mt-2 px-2 py-1.5">
                        <strong>Note:</strong> Default off for memory profiles — the senior may not respond
                        reliably to a voice nag. Enable only if you've confirmed they will.
                      </p>
                    )}
                  </div>
                  <ToggleButton
                    enabled={voiceNagEnabled}
                    onToggle={(v) => toggle('voice', v)}
                    busy={saving === 'voice'}
                  />
                </div>
              </section>

              {/* Low battery SMS toggle */}
              <section className="rounded-2xl border border-brand-border p-4 bg-white">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-brand-text leading-tight">
                      Text me when {seniorName}'s phone hits 25%
                    </h3>
                    <p className="text-xs text-brand-text-muted mt-1">
                      Single SMS at 25% unplugged — won't repeat for 4 hours.
                      Never sent while charging or if you've replied STOP.
                    </p>
                    {smsGateBlocked && (
                      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg mt-2 px-2 py-1.5">
                        {!caregiverPhone
                          ? <>Add your mobile number in <strong>My account</strong> first.</>
                          : <>You replied STOP to AidFone messages. Text START to <strong>+1 (450) 367-9692</strong> to re-opt in.</>}
                      </p>
                    )}
                  </div>
                  <ToggleButton
                    enabled={lowbatSmsEnabled}
                    onToggle={(v) => toggle('sms', v)}
                    busy={saving === 'sms'}
                    disabled={smsGateBlocked}
                  />
                </div>
              </section>

              <p className="text-xs text-brand-text-muted leading-relaxed">
                Both layers protect a different actor: the voice nag is for {seniorName}; the SMS is for you.
                They can fire on the same drop cycle.
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
};

interface ToggleButtonProps {
  enabled: boolean;
  onToggle: (next: boolean) => void;
  busy?: boolean;
  disabled?: boolean;
}

const ToggleButton: FC<ToggleButtonProps> = ({ enabled, onToggle, busy, disabled }) => {
  const handleClick = () => { if (!busy && !disabled) onToggle(!enabled); };
  const bg = disabled ? 'bg-gray-200' : enabled ? 'bg-emerald-500' : 'bg-gray-300';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-busy={busy}
      onClick={handleClick}
      disabled={busy || disabled}
      className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-200 ${bg}
                  ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                  ${busy ? 'animate-pulse' : ''}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200
                    ${enabled ? 'translate-x-6' : 'translate-x-1'}`}
      />
    </button>
  );
};

export default BatteryAlertsDrawer;
